export type DebugLevel = 'trace' | 'debug' | 'info' | 'warn' | 'error' | 'timer';

type LoggerFunction = (...messages: any[]) => void;

export interface Logger {
  trace: LoggerFunction;
  debug: LoggerFunction;
  info: LoggerFunction;
  warn: LoggerFunction;
  error: LoggerFunction;
  time: (name: string) => void;
  timeEnd: (name: string) => void;
  setLevel: (level: DebugLevel) => void;
}

const logScopes: Set<string> | undefined = import.meta.env.VITE_LOG_SCOPES
  ? new Set(import.meta.env.VITE_LOG_SCOPES.split(','))
  : undefined;

let currentLevel: DebugLevel = import.meta.env.VITE_LOG_LEVEL ?? (import.meta.env.DEV ? 'debug' : 'info');

if (import.meta.env.DEV && !import.meta.env.SSR) {
  if (logScopes) {
    console.info('LOG SCOPES', logScopes);
  }

  console.info('LOG LEVEL', currentLevel);
}

const isWorker = 'HTMLRewriter' in globalThis;
const supportsColor = !isWorker;

const timers: Record<string, number> = {};

const timeFactory = (_scope: string | undefined) => (name: string) => {
  timers[name] = performance.now();
};

const timeEndFactory = (scope: string | undefined) => (name: string) => {
  const time = performance.now() - timers[name];
  log('timer', scope, [`${name}: ${+time.toFixed(2)}ms - timer ended`]);
};

export const logger: Logger = {
  trace: (...messages: any[]) => log('trace', undefined, messages),
  debug: (...messages: any[]) => log('debug', undefined, messages),
  info: (...messages: any[]) => log('info', undefined, messages),
  warn: (...messages: any[]) => log('warn', undefined, messages),
  error: (...messages: any[]) => log('error', undefined, messages),
  time: timeFactory(undefined),
  timeEnd: timeEndFactory(undefined),
  setLevel,
};

export function createScopedLogger(scope: string): Logger {
  return {
    trace: (...messages: any[]) => log('trace', scope, messages),
    debug: (...messages: any[]) => log('debug', scope, messages),
    info: (...messages: any[]) => log('info', scope, messages),
    warn: (...messages: any[]) => log('warn', scope, messages),
    error: (...messages: any[]) => log('error', scope, messages),
    time: timeFactory(scope),
    timeEnd: timeEndFactory(scope),
    setLevel,
  };
}

function setLevel(level: DebugLevel) {
  if ((level === 'trace' || level === 'debug') && import.meta.env.PROD) {
    return;
  }

  currentLevel = level;
}

function log(level: DebugLevel, scope: string | undefined, messages: any[]) {
  const levelOrder: DebugLevel[] = ['trace', 'debug', 'info', 'timer', 'warn', 'error'];

  if (levelOrder.indexOf(level) < levelOrder.indexOf(currentLevel) || (logScopes && scope && !logScopes.has(scope))) {
    return;
  }

  if (!supportsColor) {
    console.info(`[${level.toUpperCase()}]`, ...messages);

    return;
  }

  const labelBackgroundColor = getColorForLevel(level);
  const labelTextColor = level === 'warn' ? 'black' : 'white';

  const labelStyles = getLabelStyles(labelBackgroundColor, labelTextColor);
  const scopeStyles = getLabelStyles('#77828D', 'white');

  const styles = [labelStyles];

  if (typeof scope === 'string') {
    styles.push('', scopeStyles);
  }

  console.info(`%c${level.toUpperCase()}${scope ? `%c %c${scope}` : ''}`, ...styles, ...messages);
}

function getLabelStyles(color: string, textColor: string) {
  return `background-color: ${color}; color: white; border: 4px solid ${color}; color: ${textColor};`;
}

function getColorForLevel(level: DebugLevel): string {
  switch (level) {
    case 'trace':
    case 'debug': {
      return '#77828D';
    }
    case 'info': {
      return '#1389FD';
    }
    case 'warn': {
      return '#FFDB6C';
    }
    case 'error': {
      return '#EE4744';
    }
    case 'timer': {
      return '#663399';
    }
    default: {
      return 'black';
    }
  }
}

export const renderLogger = createScopedLogger('Render');
